// @flow
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from './style.module.scss';
import MatrixResultTable from '../../containers/MatrixResultTable/MatrixResultTable';
import actionTypes from '../../store/results/actionTypes';
import Legend from '../../components/Legend';
import CustomLink from '../../components/CustomLink/Link';
import routes from '../../constants/routes';
import useWindowDimensions from '../../utils/windowDemensions';
import MetaDecorator from '../../utils/MetaDecorator';
import { preflop } from '../../utils/MetaDecorator/meta';
import { resultLegend } from '../../utils/resultColors';

const CardTablePage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isVs3bet, setVs3bet] = useState<boolean>(false);
  const [isVs4bet, setVs4bet] = useState<boolean>(false);
  const [isEasy, setIsEasy] = useState<boolean>(false);
  const [selectedTs, setSelectedTs] = useState<{ label: string, value: string }>({ label: 'Low', value: 'low' });
  const [selectedBB, setSelectedBB] = useState<{ label: string, value: string }>({ label: '100', value: '100' });
  const [gameTable, setGameTable] = useState<string>('6max');
  const [isPlo5, setPlo5] = useState<boolean>(sessionStorage.getItem('isPlo5') === 'true');
  const [ggps, setGgps] = useState<string>('ps');
  const [difficulty, setDifficulty] = useState<{ label: string, value: string }>({ label: 'Default', value: 'default' });
  // data for first table
  /// ///////////////////////////////////////
  /// ///////////////////////////////////////
  const result = useSelector((state) => state.results);
  const isResultLoading = useSelector((state) => state.api.GET_RESULTS.loading);
  // const isFreeUser = useSelector((state) => state.profile.free_access);
  const isFreeUser = localStorage.getItem('freeAccess') === 'true';

  const { user } = useSelector((state) => state.profile);
  const isTrial = user?.rate?.toLowerCase() === 'trial';

  const { width } = useWindowDimensions();

  useEffect(() => {
    dispatch({
      type: actionTypes.GET_RESULTS,
      payload: {
        ts: selectedTs.value,
        bb: selectedBB.value,
        isVs3bet,
        isVs4bet,
        isEasy,
        gameTable,
        isPlo5,
        ggps,
        isFreeUser,
        difficulty: difficulty.value,
      },
    });
  }, [selectedTs, selectedBB, isVs3bet, isVs4bet, isEasy, isPlo5, gameTable, ggps, difficulty]);

  useEffect(() => {
    function checkPlo5() {
      const item = sessionStorage.getItem('isPlo5');
      setPlo5(item === 'true');
    }

    window.addEventListener('storage', checkPlo5);

    return () => {
      window.removeEventListener('storage', checkPlo5);
    };
  }, []);

  useEffect(() => {
    setSelectedTs({ label: 'Low', value: 'low' });
  }, [gameTable]);

  useEffect(() => {
    if (ggps === 'gg') setSelectedTs({ label: 'Low', value: 'low' });
    if (ggps === 'ps') setSelectedTs({ label: 'Low', value: 'low' });
    if (ggps === 'mtt') setSelectedTs({ label: 'MTT POT', value: 'mttpot' });
    if (ggps === 'hu') setSelectedTs({ label: 'HU', value: 'hu' });
  }, [ggps]);

  useEffect(() => {
    setSelectedTs({ label: 'Low', value: 'low' });
    setSelectedBB({ label: '100', value: '100' });
  }, [isPlo5]);

  return (
    <div className={styles.home}>
      <MetaDecorator
        description={preflop.description}
        title={preflop.title}
      />
      <div
        className={styles.matrixWrapper}
      >
        <div className={styles.allTheTables}>
          {
            (width > 1060) ? (
              <div className={styles.allTheTablesIndent} />
            ) : null
          }
          <div className={styles.preflop}>
            <div className={styles.infoWrapper}>
              {
                (width > 1060) ? (
                  <div className={styles.infoWrapperIndent} />
                ) : null
              }
              <div className={styles.info}>
                <CustomLink to={routes.app.resultsFAQ.root}>{t('resultsFaqLabel', 'Results FAQ')}</CustomLink>
              </div>
              {
                (width > 1060) ? (
                  <div className={styles.infoWrapperIndent} />
                ) : null
              }
            </div>
            <div className={styles.tablesWrapper}>
              <MatrixResultTable
                title="Average last month"
                rate={!!isTrial}
                result={result?.average || []}
                isResultLoading={isResultLoading}
                selectedTs={selectedTs}
                selectedBB={selectedBB}
                setVs3bet={setVs3bet}
                isVs3bet={isVs3bet}
                setVs4bet={setVs4bet}
                isVs4bet={isVs4bet}
                setIsEasy={setIsEasy}
                isEasy={isEasy}
                setSelectedTs={setSelectedTs}
                setSelectedBB={setSelectedBB}
                isPlo5={isPlo5}
                gameTable={gameTable}
                setGameTable={setGameTable}
                ggps={ggps}
                setGgps={setGgps}
                isFreeUser={isFreeUser}
                difficulty={difficulty}
                setDifficulty={setDifficulty}
              />

              <MatrixResultTable
                title="Best results"
                rate={!!isTrial}
                result={result?.best || []}
                isResultLoading={isResultLoading}
                selectedTs={selectedTs}
                selectedBB={selectedBB}
                setVs3bet={setVs3bet}
                isVs3bet={isVs3bet}
                setVs4bet={setVs4bet}
                isVs4bet={isVs4bet}
                setIsEasy={setIsEasy}
                isEasy={isEasy}
                setSelectedTs={setSelectedTs}
                setSelectedBB={setSelectedBB}
                isPlo5={isPlo5}
                gameTable={gameTable}
                setGameTable={setGameTable}
                ggps={ggps}
                setGgps={setGgps}
                isFreeUser={isFreeUser}
                difficulty={difficulty}
                setDifficulty={setDifficulty}
              />
            </div>
          </div>
        </div>

        <div className={styles.legenda}>
          {resultLegend.map((item) => (
            <Legend
              key={item.name}
              name={item.name}
              color={item.color}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CardTablePage;
